import { ColumnFormat } from '@/modules/core/app/constants/data.constants';

export const TOOLTIP_NUMBER_FORMAT = '#,###.##';
export const TOOLTIP_DURATION_FORMAT = 'hh:mm:ss';
export const LABEL_DURATION_FORMAT = 'mm:ss';
export const ELLIPSIS = '...';
export const MAX_LABEL_COUNT = 10;
export function GetHandMissingWarning() {
  return {
    title: 'Metrics order adjustment needed! ',
    content: 'The scale value is lower than the hand metric value. Please rearrange accordingly.',
  };
}
export function GetFunnelTooManyLabels() {
  return i18n.$t('Metric labels are currently\nhidden due to space constraints\nin the widget');
}
export function GetGaugeTooManyHandsForBigNumber() {
  return i18n.$t('Big Number can only\ndisplay inside hands\nwith one metric selected');
}

export const USE_DATE_AXIS_AS_CATEGORY = true;

export function GetZeroDataMessageGaugeChart() {
  return {
    TOOLTIP: i18n.$t(
      `\n The current target for this gauge is zero, which is a valid configuration and not an error. \n The gauge won’t display a KPI comparison against a target of zero. \n If this isn’t what you expected, consider updating your target to a non-zero value if appropriate.`
    ),
    MODAL_CONTENT: `<p>${i18n.$t(
      "This gauge is displaying a zero target value as expected. If you didn't intend for a zero target, <br>please update your target metric to a non-zero value"
    )}.</p>`,
    BUTTON_ID: 'okButton_',
    REPORT_STUDIO_NOTE_FOR_ZERO_VALUES: i18n.$t(
      "NOTE: This gauge is displaying a zero target value as expected. If you didn't intend for a zero target, please update your target metric to a non-zero value."
    ),
  };
}

export const SeriesType = {
  LINE: 'line',
  COLUMN: 'column',
  STEPLINE: 'stepline',
  CANDLESTICK: 'candlestick',
  OHLC: 'ohlc',
  SMOOTHEDLINE_X: 'smoothedline_x',
  SMOOTHEDLINE_Y: 'smoothedline_y',
  SMOOTHEDLINE_XY: 'smoothedline_xy',
  STEP_LINE: 'step_line',
};
export const FontStyle = {
  BOLD: 'bold',
  TEXT: 'text',
};
export const Position = {
  LEFT: 'left',
  RIGHT: 'right',
  TOP: 'top',
  BOTTOM: 'bottom',
  ABSOLUTE: 'absolute',
  MIDDLE: 'middle',
  END: 'end',
};
export const ChartPlace = {
  REPORT: 'Report',
  DASHBOARD: 'Dashboard',
};
export const ChartConfig = {
  CHART_PLACE: 'chartPlace',
};
export const BackgroundGradientType = {
  SOLID: 'solid',
  LINEAR_GRADIENT: 'linear',
  LINEAR_Y_GRADIENT: 'linear-y',
  RADIAL_GRADIENT: 'radial',
};
export const ValueFormatters = {
  [ColumnFormat.FORMAT_INTEGER]: {
    numberFormat: '#,###.##a',
    // Group only into M (millions), and B (billions)
    bigNumberPrefixes: [
      { number: 1e3, suffix: 'k' }, // Thousand
      { number: 1e6, suffix: 'M' }, // Million
      { number: 1e9, suffix: 'B' }, // Billion
      { number: 1e12, suffix: 'T' }, // Trillion
      { number: 1e15, suffix: 'P' }, // Quadrillion (Peta)
      { number: 1e18, suffix: 'E' }, // Quintillion (Exa)
    ],
  },
  [ColumnFormat.FORMAT_PERCENT]: {
    numberFormat: '#,###.##%',
  },
  [ColumnFormat.FORMAT_CURRENCY]: {
    numberFormat: '#,###.00',
  },
  [ColumnFormat.FORMAT_DECIMAL]: {
    numberFormat: '.00',
  },
  display_percent: {
    numberFormat: "#'%'",
  },
  SINGLE_PRECISION: '#,###.#a',
  SMALL_NUMBER: '#.###',
  TOO_SMALL_NUMBER: '#.###a',
  MULTIPLE_PRECISION: '#,###.##',
  SINGLE_NUMBER_FORMAT: '#a',
  ABBR_CURRENCY: '#,###.00',
  SINGLE_PERCENT: '0.##p',
};

export const AxisFormatters = {
  [ColumnFormat.FORMAT_INTEGER]: {
    numberFormat: '#,###.#a',
  },
  [ColumnFormat.FORMAT_CURRENCY]: {
    numberFormat: '#.##a',
  },
  [ColumnFormat.FORMAT_PERCENT]: {
    numberFormat: '#p',
  },
  [ColumnFormat.FORMAT_DECIMAL]: {
    numberFormat: '#,###.##a',
  },
  FORMAT_CURRENCY: '#.##a',
};

export const ValueSymbol = {
  [ColumnFormat.FORMAT_PERCENT]: '%',
  LINE_CHART_STACKED_PTS: '%pts',
};

export const BulletShapes = {
  CIRCLE: 'circle',
  DIAMOND: 'diamond',
  STAR: 'star',
  TRIANGLE: 'triangle',
  ASTERISK: 'star',
  TRIANGLE_UP: 'triangle',
  TRIANGLE_DOWN: 'triangle-down',
  TRIANGLE_LEFT: 'triangle-left',
  TRIANGLE_RIGHT: 'triangle-right',
  SQUARE: 'square',
};

export const LineTypes = {
  LINE: 'line',
  STEP_LINE: 'step_line',
};

export const YAxisPosition = {
  DEFAULT: 'default',
  SWAP: 'swap_y_axes',
  LEFT: 'all_left',
  RIGHT: 'all_right',
};

export const BubbleShapes = {
  CIRCLE: 'circle',
  DIAMOND: 'diamond',
  STAR: 'star',
  TRIANGLE: 'triangle',
};

export const HandTypes = {
  NONE: 'none',
  NEEDLE: 'needle',
  POINTER: 'pointer',
  TRIANGLE: 'triangle',
  LINE: 'line',
};

export const GaugeBigNumberPosition = {
  NONE: 'none',
  HAND: 'hand',
  HAND_TRANSPARENT: 'hand_transparent',
  INSIDE: 'inside',
  BELOW: 'below',
};

export const GaugeBigNumberInteriorPosition = {
  LEFT: 'left',
  RIGHT: 'right',
  CENTER: 'center',
};

export const GaugeBigNumberInteriorPositionYPosMap = {
  240: -50,
  255: -50,
  270: -40,
  285: -30,
  300: -25,
  315: -20,
  320: -15,
};

export const GaugeBigNumberBelowPositionYPosMap = {
  195: 30,
  210: 50,
  225: 60,
  240: 90,
  255: 90,
  270: 100,
  285: 130,
  300: 140,
  315: 140,
  320: 140,
};

export const GaugeBigNumberLengthToWidthMap = {
  1: 20,
  2: 30,
  3: 40,
  4: 50,
  5: 60,
  6: 70,
  7: 70,
  8: 75,
  9: 75,
};

export const GaugeBigNumberLengthToRadiusMap = {
  9: 28,
  10: 30,
  11: 33,
  12: 35,
};

export const GaugeBigNumberLengthToFontSizeMap = {
  1: 1.2,
  2: 1.2,
  3: 1.2,
  4: 1.2,
  5: 1.1,
  6: 1,
  7: 0.9,
  8: 0.8,
};

export const ExportingFormats = {
  PNG: 'png',
  JPG: 'jpg',
  CANVAS: 'canvas',
  PDF: 'pdf',
  XLSX: 'xlsx',
  CSV: 'csv',
  JSON: 'json',
  HTML: 'html',
  PDF_DATA: 'pdfdata',
  PRINT: 'print',
};

// alphabetise, please!
export const Constant = {
  ONE_EM: '1em',
  ONE_POINT_ONE: '1.1em',
  ONE_POINT_TWO: '1.2em',
  ADJUSTED: 'adjusted',
  ANIMATION_DURATION: 'animationDuration',
  ARC_DIAGRAM: 'ArcDiagram',
  AXIS: 'axis',
  AXIS_FILL: 'axisFill',
  BACKGROUND: 'background',
  BENCHMARK: 'Benchmark (All Data)',
  BENCHMARK_INSTANCE: 'Benchmark (Instance)',
  BENCHMARK_PREFIX: 'benchmark_',
  BENCHMARK_INSTANCE_PREFIX: 'benchmark_instance_',
  BENCHMARKS_APPLIED: 'benchmarksApplied',
  BENCHMARK_IDENTIFIER: 'all',
  BENCHMARK_INSTANCE_IDENTIFIER: 'instance',
  BLACK_TEXT_COLOR: '#000000',
  BOLD: '[bold]',
  BOTTOM_WIDTH: 'bottomWidth',
  BREAK: 'break',
  BULLET: 'bullet',
  CATEGORY: 'category',
  CATEGORY_FIELD: 'categoryField',
  CELL_WIDTH: 'cellWidth',
  CELL_START_LOCATION: 'cellStartLocation',
  CELL_END_LOCATION: 'cellEndLocation',
  CENTER: 'center',
  CENTER_X: 'centerX',
  CHART_PLACE: 'chartPlace',
  CHART_TRANSPARENCY: 'chartTransparency',
  CLICK: 'click',
  COLOR: 'color',
  COLUMN_SETTINGS: 'columnSettings',
  COMPARISON: '_comparison',
  COMPARISON_TEXT: 'Comparison',
  COMPONENT: 'Component',
  CORNER_RADIUS_BR: 'cornerRadiusBR',
  CORNER_RADIUS_TL: 'cornerRadiusTL',
  CLIENT_CURRENCY_SYMBOL: 'clientCurrencySymbol',
  CURRENT_PERIOD: 'Current Period',
  CURSOR: 'cursor',
  DATA_VALIDATED: 'datavalidated',
  DAY: 'day',
  DEFAULT: 'default',
  DEFAULT_FONT_SIZE: 12,
  ROTATED_FONT_SIZE: 11,
  DEFAULT_CENTER_Y: 26,
  DISABLED: 'disabled',
  DRAW: 'draw',
  DISPLAY_PERCENT: 'display_percent',
  END: 'end',
  END_INDEX: 'endIndex',
  ENTITY: 'Entity',
  EXPORT_STARTED: 'exportstarted',
  EXPORT_FINISHED: 'exportfinished',
  ELLIPSIS: '...',
  FILL: 'fill',
  FILL_OPACITY: 'fillOpacity',
  FILL_GRADIENT: 'fillGradient',
  FOCUS: 'focus',
  FONT_SIZE: 'fontSize',
  FONT_SIZE_MEDIUM: 16,
  FONT_SIZE_18: 18,
  FONT_SIZE_LARGE: 21,
  FONT_SIZE_LARGEST: 36,
  FORCE_HIDDEN: 'forceHidden',
  FORMATTED: 'formatted_',
  FRAME_ENDED: 'frameended',
  FUNNEL_SERIES: 'FunnelSeries',
  PICTORIAL_STACKED_SERIES: 'PictorialStackedSeries',
  PYRAMID_SERIES: 'PyramidSeries',
  GREY: '#EEEEEE',
  GRID_STROKE_COLOR: '#cecece',
  HEAT_RULES: 'heatRules',
  HEIGHT: 'height',
  HIERARCHY: 'Hierarchy',
  HIDDEN: 'hidden',
  HORIZONTAL: 'horizontal',
  HOVER: 'hover',
  LABEL: 'label',
  LABEL_HTML: 'labelHTML',
  LABEL_TEXT: 'labelText',
  LIGHTER: 'lighter',
  LOG_DATE: 'log_date',
  METRIC_VALUE: 'metricValue',
  MIN: 'min',
  MAX: 'max',
  MAX_WIDTH: 'maxWidth',
  PIE_METRIC: 'metric',
  PIE_RAW_METRIC: 'rawMetric',
  POINT: 'point',
  LOCATION_X: 'locationX',
  LOCATION_Y: 'locationY',
  MARKER: 'marker',
  MAP_CHART: 'MapChart',
  MIDDLE: 'middle',
  MONTH: 'month',
  NA: 'NA',
  NAME: 'name',
  NONE: 'none',
  OPACITY: 'opacity',
  OPEN_VALUE_FIELD_NAME: 'rankingMetricMaxValue',
  OPEN_VALUE_X: 'openValueXField',
  OPEN_VALUE_Y: 'openValueYField',
  OTHERS: 'Others',
  PAN_X: 'panX',
  PAN_Y: 'panY',
  POINTER: 'pointer',
  POINTER_OUT: 'pointerout',
  POINTER_OVER: 'pointerover',
  POINTER_LENGTH: 'pointerLength',
  POLYGON: 'Polygon',
  PRIOR_PERIOD: 'Prior Period',
  QUARTER: 'quarter',
  RADIUS: 'radius',
  RENDERER: 'renderer',
  RIGHT: 'right',
  ROTATION: 'rotation',
  SPRITE: 'sprite',
  SPRITE_ELEMENT: 'Sprite',
  TEXT: 'text',
  RAW_VALUE: 'rawValue',
  SCROLLBAR: 'Scrollbar',
  SCROLLBAR_X: 'scrollbarX',
  SCROLLBAR_Y: 'scrollbarY',
  SECOND: 'second',
  SERIES_U: 'Series',
  SLICE: 'slice',
  START_INDEX: 'startIndex',
  TEMPLATE_FIELD: 'templateField',
  TICK: 'tick',
  TOOLTIP: 'tooltip',
  TOOLTIP_DATA_ITEM: 'tooltipDataItem',
  TOOLTIP_ELEMENT: 'Tooltip',
  TOOLTIP_TEXT: 'tooltipText',
  TOP_WIDTH: 'topWidth',
  TRUNCATE: 'truncate',
  UNDEFINED: 'undefined',
  USER_DATA: 'userData',
  VALUE: 'value',
  VALUE_HIGH: 'valueHigh',
  VALUE_PCT_TOTAL: 'valuePercentTotal',
  VALUE_X: '{valueX}',
  VALUE_X_FIELD: 'valueXField',
  VALUE_X_SHOW: 'valueXShow',
  VALUE_X_TOTAL_PERCENT: 'valueXTotalPercent',
  VALUE_Y: '{valueY}',
  VALUE_Y_FIELD: 'valueYField',
  VALUE_Y_SHOW: 'valueYShow',
  VALUE_Y_TOTAL_PERCENT: 'valueYTotalPercent',
  VALUE_WORKING: 'valueWorking',
  VISIBLE: 'visible',
  VERTICAL: 'vertical',
  VERTICAL_SCROLLBAR: 'verticalScrollbar',
  VS: 'vs',
  WHEEL_DURATION: 'wheelDuration',
  WHITE_TEXT: '#FFFFFF',
  WIDGET: 'widget',
  WIDTH: 'width',
  WORD_CLOUD: 'WordCloud',
  WRAP: 'wrap',
  X_AXES: 'xAxes',
  Y: 'y',
  Y_AXES: 'yAxes',
  ZOOM_X: 'zoomX',
  ZOOM_Y: 'zoomY',
  ZOOM_XY: 'zoomXY',
  NOT_APPLICABLE: 'N/A',
  STRING: 'string',
  NAN: 'NaN',
  MASK_CONTENT: 'maskContent',
  COLUMN_SERIES: 'columnSeries',
  GRAPHICS: 'graphics',
  DX: 'dx',
};

export const OversizedBehaviors = {
  TRUNCATE: 'truncate',
  FIT: 'fit',
};

export const LABEL_PLACEHOLDERS = {
  CATEGORY: '{category}',
  METRIC: '{metric}',
  PERCENT: "{valuePercentTotal.formatNumber('0.00')}%[/]",
  PERCENT_NO_END: "{valuePercentTotal.formatNumber('0.00')}%",
  PIE_PERCENT: "{rawValue.formatNumber('0.00')}%[/]",
  PIE_PERCENT_NO_END: "{rawValue.formatNumber('0.00')}%",
  TOOLTIP_PERCENT_NO_END: "{value.formatNumber('0.00')}%",
  VALUE: '{value}',
  END_VALUE: '{endValue}',
  RAW_VALUE: '{rawValue}',
  TOOLTIP_TEXT: '{tooltipText}',
  TOOLTIP_VALUE: '<TEXT>',
};
export const LABEL_CUTOFFS = {
  30: 3,
  20: 2,
};
export const WidgetEvents = {
  FRAME_ENDED: 'frame-ended',
  BOUNDSCHANGED: 'boundschanged',
  IMAGE_EXPORTED: 'imageExported',
};

export const ButtonConstant = {
  COLOR: '#737C84',
  RESET_VIEW: 'Reset View',
  DRILLDOWN_BACK: '< Back',
};

export const PointerOrientation = {
  HORIZONTAL: 'horizontal',
  VERTICAL: 'vertical',
  DEFAULT: 'default',
};

export const DefaultShadowSettings = {
  shadowBlur: 10,
  shadowOffsetX: 4,
  shadowOffsetY: 4,
  shadowOpacity: 0.5,
};

export const PieChartConstant = {
  LEGEND_TEMPLATE: 'legendData',
  SERIES: 'Series',
  CATEGORY: 'category',
  CATEGORY_X: 'categoryX',
  CATEGORY_Y: 'categoryY',
  DONUT_FONT_SIZE_MODIFIER: 8,
  FILL: 'fill',
  FIT: 'fit',
  PRIOR_PERIOD: 'Prior Period',
  LABEL_FONT_WEIGHT: '800',
  LABEL_FONT_SIZE: 14,
  PLOT_DONUT: 'donut(v2)',
  DEFAULT_INNER_RADIUS: 40,
  PLOT_V2_PIE: 'piechart(v2)',
  ZERO: 0,
  DONUT_SUM: "{valueSum.formatNumber('#,###.##a')}",
  OVERSIZED_DONUT_SUM: 'fit',
  DONUT_MAXWIDTH: 'maxWidth',
  DONUT_VALUE_SUM: 'valueSum',
  WRAP: 'wrap',
  LEGEND_LABEL_TEXT: '{category}\t\t\t\t[/]',
  LEGEND_VALUE_TEXT: "{value.formatNumber('#,###.##a')}",
  TOGGLE_KEY: 'toggleKey',
  NONE: 'none',
  LEGEND_RAW_VALUE_TEXT: "{rawValue.formatNumber('#,###.##a')}",
  ACTIVE: 'active',
  COMPACT_FORMAT: '#,###.##a',
  WHITE_TEXT_COLOR: '#fff',
  BLACK_TEXT_COLOR: '#000',
};

export const ThemeRules = {
  GRID: 'Grid',
  INTERFACE_COLORS: 'InterfaceColors',
};

export const ThemeClassElements = {
  AXIS_LABEL: 'AxisLabel',
  FUNNEL: 'FunnelSeries',
  LABEL: 'Label',
  LEGEND: 'Legend',
  RADIAL_LABEL: 'RadialLabel',
  TOOLTIP: 'Tooltip',
};

export const ThemeClassTags = {
  AXIS: 'axis',
  CIRCULAR: 'circular',
  FUNNEL: 'funnel',
  LABEL: 'label',
  LEGEND: 'legend',
  PIE: 'pie',
  SERIES: 'series',
  TOOLTIP: 'tooltip',
};

export const PictorialTypes = {
  MALE: 'male',
  MUG: 'mug',
  FEMALE: 'female',
  KEY: 'key',
  APPLE: 'apple',
  MOUNTAIN: 'mountain',
  SAILBOAT: 'sailboat',
  BULB: 'bulb',
  THERMOMETER: 'thermometer',
  TREE: 'tree',
  ROCKETSHIP: 'rocketship',
  HAMMER: 'hammer',
  SKYSCRAPER: 'skyscraper',
  EVERGREEN: 'evergreen',
  CUP: 'cup',
  HEART: 'heart',
  HOUSE: 'house',
};

export const PictorialSvg = {
  MALE: 'M53.5,476c0,14,6.833,21,20.5,21s20.5-7,20.5-21V287h21v189c0,14,6.834,21,20.5,21 c13.667,0,20.5-7,20.5-21V154h10v116c0,7.334,2.5,12.667,7.5,16s10.167,3.333,15.5,0s8-8.667,8-16V145c0-13.334-4.5-23.667-13.5-31 s-21.5-11-37.5-11h-82c-15.333,0-27.833,3.333-37.5,10s-14.5,17-14.5,31v133c0,6,2.667,10.333,8,13s10.5,2.667,15.5,0s7.5-7,7.5-13 V154h10V476 M61.5,42.5c0,11.667,4.167,21.667,12.5,30S92.333,85,104,85s21.667-4.167,30-12.5S146.5,54,146.5,42 c0-11.335-4.167-21.168-12.5-29.5C125.667,4.167,115.667,0,104,0S82.333,4.167,74,12.5S61.5,30.833,61.5,42.5z',
  FEMALE:
    'M 18.4 15.1 L 15.5 25.5 c -0.6 2.3 2.1 3.2 2.7 1 l 2.6 -9.6 h 0.7 l -4.5 16.9 H 21.3 v 12.7 c 0 2.3 3.2 2.3 3.2 0 V 33.9 h 1 v 12.7 c 0 2.3 3.1 2.3 3.1 0 V 33.9 h 4.3 l -4.6 -16.9 h 0.8 l 2.6 9.6 c 0.7 2.2 3.3 1.3 2.7 -1 l -2.9 -10.4 c -0.4 -1.2 -1.8 -3.3 -4.2 -3.4 h -4.7 C 20.1 11.9 18.7 13.9 18.4 15.1 z M 28.6 7.2 c 0 -2.1 -1.6 -3.7 -3.7 -3.7 c -2 0 -3.7 1.7 -3.7 3.7 c 0 2.1 1.6 3.7 3.7 3.7 C 27 10.9 28.6 9.2 28.6 7.2 z',
  MUG: 'M246.146,61.435c-3.416,0-6.744,0.326-9.981,0.946c-5.445,1.033-9.866-2.105-10.356-8.088 c-0.114-1.42-0.234-2.752-0.348-3.981c-0.566-5.983-6.119-10.802-12.124-10.802H17.819c-6.005,0-11.988,4.748-13.108,10.65 c-21.577,113.655,36.708,182.214,58.851,203.568c4.324,4.177,13.01,7.223,19.015,7.223h70.164c6.005,0,14.62-3.127,18.884-7.364 c22.665-22.512,36.36-51.296,44.475-80.411c1.615-5.787,6.88-7.963,12.564-6.01c5.488,1.882,11.362,2.91,17.481,2.91 c30.002,0,54.315-24.318,54.315-54.314C300.46,85.753,276.148,61.435,246.146,61.435z M246.146,145 c-4.058,0-7.919-0.827-11.433-2.322c-5.532-2.355-8.561-10.851-8.023-16.839l2.127-23.508c0.544-5.988,4.194-13.163,8.773-14.56 c2.709-0.827,5.575-1.267,8.55-1.267c16.154,0,29.246,13.092,29.246,29.246C275.391,131.909,262.3,145,246.146,145z',
  KEY: 'M256.001,0c-76.388,0-138.316,61.922-138.316,138.316c0,62.193,41.052,114.792,97.528,132.192v216.898 c0,3.876,1.855,7.512,5.001,9.782l17.354,12.527c5.31,3.839,12.724,2.731,16.684-2.504l42.342-56.008 c3.258-4.306,3.258-10.25,0-14.557l-16.442-21.699v-0.022l16.427-21.684c3.273-4.306,3.265-10.265,0-14.571l-10.846-14.308 c-3.288-4.344-3.258-10.355,0.075-14.67l27.952-36.112c1.644-2.112,2.534-4.706,2.534-7.383V262.83 c46.181-22.4,78.023-69.735,78.023-124.514C394.316,61.922,332.395,0,256.001,0z M256.001,468.143h-14.541V275.87 c4.781,0.498,9.631,0.762,14.541,0.762V468.143z M256.906,97.634c-15.975,0-28.932-12.95-28.932-28.924 c0-15.982,12.957-28.932,28.932-28.932c15.974,0,28.931,12.949,28.931,28.932C285.837,84.683,272.879,97.634,256.906,97.634z',
  APPLE:
    'M22.3,8.1c-1.8-0.3-3.5,0.1-5,1.1c-0.1,0.1-0.2,0.1-0.3,0.1c-0.1-0.5-0.2-1-0.3-1.4c0.5,0.1,1,0.2,1.5,0.2 c0.5,0,0.9-0.1,1.4-0.2c0,0,0,0,0,0c2.1-0.8,3-3.4,3-3.7c0.1-0.4,0-0.9-0.4-1.1c-0.3-0.2-2.6-1.6-4.7-0.7c-1.2,0.4-2,1.4-2.4,2.3 c-0.6-0.8-1.2-1.5-2-2.1l-0.4-0.3c-0.4-0.3-1.1-0.2-1.4,0.2c-0.3,0.4-0.2,1.1,0.2,1.4l0.4,0.3c1.7,1.2,2.8,3.1,3.1,5.1 c0,0-0.1,0-0.1-0.1c-1.5-1-3.3-1.3-5-1.1c-4.3,0.7-7.3,5.1-6.6,9.8C3.5,21.4,6,26.5,9,28.8c1,0.7,2,1.1,3,1.1c0.2,0,0.4,0,0.6,0 c0.6-0.1,1.3-0.4,1.8-0.8c0.9-0.7,2.3-0.7,3.2,0c0.6,0.4,1.2,0.7,1.8,0.8c1.2,0.2,2.4-0.2,3.6-1.1c3-2.2,5.5-7.4,5.9-10.9 C29.5,13.2,26.5,8.8,22.3,8.1z',
  MOUNTAIN:
    'M8.012,1.062 L4.035,8.87 L2.709,7.569 C2.709,7.569 -0.305,14 0.063,14 L15.965,14 L15.965,13.998 L12.627,6.898 L11.644,7.51 L8.012,1.062 L8.012,1.062 Z M5.611,7.521 L8.062,2.77 L10.347,6.851 L9.361,7.521 L8.021,6.233 L5.611,7.521 L5.611,7.521 Z',
  SAILBOAT:
    'M 181.847656 685.453125 L 635.4375 685.453125 L 557.359375 756.066406 L 254.375 756.066406 Z M 181.847656 685.453125 M 400.664062 462.222656 L 537.425781 683.363281 L 400.664062 683.363281 Z M 400.664062 462.222656 M 397.777344 508.921875 L 320.886719 683.363281 L 397.777344 683.363281 Z M 397.777344 508.921875',
  BULB: 'M513.15,18.511v0.021c-164.148,0-282.927,56.123-361.045,139.248S36.507,350.801,33.885,458.062  c-2.625,107.397,35.309,204.707,78.402,285.292c43.093,80.584,91.455,144.849,109.574,184.568  c17.133,37.557,14.765,80.31,17.927,123.251c3.162,42.941,12.338,86.31,53.473,123.087c0.627,0.567,1.418,0.919,2.259,1.005  c0.119,0.01,0.23,0.01,0.35,0.021l-0.123,16.407l11.274,26.552c1.791,14.202,1.032,21.956-0.657,29.201  c-11.924,9.146-22.498,24.123,9.959,32.527v21.911c-33.898,15.394-22.22,31.257,0,43.144l-1.992,15.935  c-31.658,11.753-19.731,34.539,1.314,45.793v14.6c-26.551,11.505-25.874,32.759,0.678,47.805v22.568l65.712,61.728l47.128,47.128  l1.335,17.927h167.935v-14.6l53.76-50.454c12.593-3.704,41.688-29.798,78.998-67.047l4.538-16.921l-15.976-11.109l0.473-14.005  c27.743-5.77,31.03-38.316,0-43.596v-15.914c37.813-7.84,28.739-35.739,3.306-41.83v-13.266  c45.388-18.835,25.242-39.576,4.661-44.479v-21.911c26.391-4.442,26.016-34.883,7.495-44.191l8.419-17.742l0.575-20.905  c1.516-0.04,3.043-0.079,4.559-0.159c0.815-0.022,1.604-0.294,2.259-0.779c35.795-26.773,44.31-67.235,46.799-110.129  c2.49-42.894-0.722-88.846,10.391-128.302c13.188-46.829,57.526-100.909,99.964-172.288c42.439-71.38,82.806-160.426,87.479-278.166  c4.679-117.883-33.759-235.943-114.626-324.576C800.638,77.511,677.344,18.517,513.15,18.517L513.15,18.511z',
  THERMOMETER:
    'M12 15.9998C11.4477 15.9998 11 16.4475 11 16.9998C11 17.5521 11.4477 17.9998 12 17.9998C12.5523 17.9998 13 17.5521 13 16.9998C13 16.4475 12.5523 15.9998 12 15.9998ZM12 15.9998V6M12 16.9998L12.0071 17.0069M16 16.9998C16 19.209 14.2091 20.9998 12 20.9998C9.79086 20.9998 8 19.209 8 16.9998C8 15.9854 8.37764 15.0591 9 14.354L9 6C9 4.34315 10.3431 3 12 3C13.6569 3 15 4.34315 15 6V14.354C15.6224 15.0591 16 15.9854 16 16.9998Z',
  EVERGREEN:
    'M61 53.684l-12.88-8.936c4.842-.894 8.047-1.832 8.047-1.832l-10.834-9.02c3.628-.865 6.001-1.735 6.001-1.735l-9.123-9.523c2.6-.894 4.289-1.771 4.289-1.771L31.999 2L17.5 20.867s1.689.877 4.288 1.771l-9.121 9.523s2.372.87 5.999 1.734L7.833 42.916s3.205.938 8.046 1.832L3 53.684s10.053 2.456 22.233 3.209V62h13.534v-5.107C50.946 56.14 61 53.684 61 53.684z',
  ROCKETSHIP:
    'M60,48l-8-4.57c0-4.027,0-8.047,0-11.43c0-12-8-24-20-32C20,8,12,20,12,32c0,3.383,0,7.402,0,11.43L4,48 c-2.426,1.27-4,2.977-4,5.188V60c0,2.211,1.789,4,4,4h56c2.211,0,4-1.789,4-4v-6.812C64,50.977,62.125,49.375,60,48z M32,32 c-2.211,0-4-1.789-4-4s1.789-4,4-4s4,1.789,4,4S34.211,32,32,32z',
  HAMMER:
    'M423.29,90.281C398.149,53.719,351.306,0,280.071,0c-39.609,0-86.875,0-86.875,0 c-3.578,0-6.484,2.906-6.484,6.469v3.063c0,3.563-2.891,6.469-6.469,6.469h-20.563c-3.578,0-6.469-2.906-6.469-6.469V6.469 c0-3.563-2.906-6.469-6.484-6.469H96.431C89.29,0,83.478,5.813,83.478,12.969v64.344c0,7.172,5.813,12.969,12.953,12.969h50.297 c3.578,0,6.484-2.906,6.484-6.469v-3.047c0-3.578,2.891-6.484,6.469-6.484h20.563c3.578,0,6.469,2.906,6.469,6.484v9.516v52.578 h91.438v-26.656c0-14.313,11.609-25.922,25.906-25.922c0,0,6.641,0,7.234,0c48,0,73.156,10.281,97.156,20.563 C423.728,117.406,435.868,104,423.29,90.281z M 266.056 512 C 271.29 512 276.29 509.716 279.962 505.671 C 283.618 501.609 285.603 496.125 285.493 490.474 L 278.149 141.891 L 186.711 141.891 L 179.383 490.474 C 179.258 496.126 181.258 501.609 184.914 505.671 C 188.57 509.715 193.586 512 198.805 512 L 266.056 512 Z',
  SKYSCRAPER:
    'm480.8 9.7982v88.03h-6.9832l-4.0256 154-16.103 42.844h-12.159v14.911h-11.461v65.15h-9.6533v80.964h-6.6957v360.54h-14.788v83.635h-30.028v122.91h-36.231v56.318h305.87v-56.318h-36.19v-122.91h-30.069v-83.635h-14.747v-360.54h-6.7368v-80.964h-9.6533v-65.15h-11.584v-14.911h-12.036l-16.061-42.844-5.258-154h-5.7509v-88.03h-9.6533zm193.27 299.83v59.563',
  TREE: 'M537.804,174.688c0-44.772-33.976-81.597-77.552-86.12c-12.23-32.981-43.882-56.534-81.128-56.534 c-16.304,0-31.499,4.59-44.514,12.422C319.808,17.949,291.513,0,258.991,0c-43.117,0-78.776,31.556-85.393,72.809 c-3.519-0.43-7.076-0.727-10.71-0.727c-47.822,0-86.598,38.767-86.598,86.598c0,2.343,0.172,4.638,0.354,6.933 c-24.25,15.348-40.392,42.333-40.392,73.153c0,27.244,12.604,51.513,32.273,67.387c-0.086,1.559-0.239,3.107-0.239,4.686 c0,47.822,38.767,86.598,86.598,86.598c14.334,0,27.817-3.538,39.723-9.696c16.495,11.848,40.115,26.67,51.551,23.715 c0,0,4.255,65.905,3.337,82.64c-1.75,31.843-11.303,67.291-18.025,95.979h104.117c0,0-15.348-63.954-16.018-85.307 c-0.669-21.354,6.675-60.675,6.675-60.675l36.118-37.36c13.903,9.505,30.695,14.908,48.807,14.908 c44.771,0,81.597-34.062,86.12-77.639c32.98-12.23,56.533-43.968,56.533-81.214c0-21.994-8.262-41.999-21.765-57.279 C535.71,195.926,537.804,185.561,537.804,174.688z M214.611,373.444c6.942-6.627,12.766-14.372,17.212-22.969l17.002,35.62 C248.816,386.096,239.569,390.179,214.611,373.444z M278.183,395.438c-8.798,1.597-23.782-25.494-34.416-47.517 c11.791,6.015,25.102,9.477,39.254,9.477c3.634,0,7.201-0.296,10.72-0.736C291.006,374.286,286.187,393.975,278.183,395.438z M315.563,412.775c-20.35,5.651-8.167-36.501-2.334-60.904c4.218-1.568,8.301-3.413,12.183-5.604 c2.343,17.786,10.069,33.832,21.516,46.521C337.011,401.597,325.593,409.992,315.563,412.775z',
  CUP: 'M 539.265625 471.617188 C 541.179688 472.148438 543.789062 474.636719 544.519531 476.59375 C 544.886719 477.53125 545.90625 484.097656 546.804688 491.152344 C 547.699219 498.207031 548.515625 504.609375 548.636719 505.304688 L 548.882812 506.648438 L 578.960938 506.648438 L 577.085938 510.402344 C 572.195312 520.351562 572.152344 521.738281 576.757812 531.402344 L 578.960938 536.011719 L 571.132812 536.09375 L 563.351562 536.214844 L 559.398438 599.628906 C 557.195312 634.5 555.238281 666.429688 554.996094 670.589844 C 554.75 674.75 554.382812 680.988281 554.140625 684.457031 C 553.9375 687.921875 553.570312 693.753906 553.324219 697.382812 C 553.121094 701.054688 552.714844 706.96875 552.507812 710.554688 C 552.265625 714.144531 551.898438 720.015625 551.695312 723.605469 C 551.449219 727.195312 551.082031 733.066406 550.878906 736.65625 C 550.632812 740.246094 550.269531 746.15625 550.0625 749.789062 C 549.820312 753.457031 549.453125 759.328125 549.25 762.835938 C 549.003906 766.386719 548.636719 772.21875 548.433594 775.804688 C 548.1875 779.394531 547.824219 785.265625 547.617188 788.855469 C 547.046875 797.992188 546.4375 807.574219 545.988281 814.957031 C 545.785156 818.421875 545.417969 824.292969 545.171875 828.003906 C 544.929688 831.714844 544.601562 837.386719 544.359375 840.648438 C 544.15625 843.910156 542.035156 877.921875 539.671875 916.296875 L 535.351562 986.03125 L 534.25 988.277344 C 533.394531 989.949219 532.660156 990.804688 531.152344 991.742188 L 529.15625 992.964844 L 417.449219 993.089844 L 305.738281 993.167969 L 303.578125 992.109375 C 301.054688 990.84375 299.179688 988.359375 298.8125 985.664062 C 298.6875 984.769531 296.652344 953.246094 294.328125 915.683594 C 291.964844 878.125 289.886719 844.523438 289.640625 841.054688 C 289.4375 837.589844 289.070312 831.757812 288.828125 828.085938 C 287.890625 813.121094 287.605469 808.105469 287.195312 801.988281 C 286.992188 798.480469 286.625 792.566406 286.382812 788.855469 C 286.136719 785.144531 285.769531 779.191406 285.566406 775.601562 C 285.320312 772.011719 284.953125 766.140625 284.75 762.550781 C 284.507812 758.964844 284.140625 753.089844 283.9375 749.5 C 283.691406 745.914062 283.324219 740.121094 283.121094 736.65625 C 282.917969 733.1875 282.550781 727.398438 282.304688 723.808594 C 282.101562 720.222656 281.734375 714.347656 281.492188 710.757812 C 281.285156 707.171875 280.921875 701.214844 280.675781 697.503906 C 280.429688 693.792969 280.0625 687.921875 279.859375 684.457031 C 279.617188 680.988281 279.25 674.75 279.003906 670.589844 C 278.761719 666.429688 276.804688 634.5 274.601562 599.628906 L 270.648438 536.214844 L 262.867188 536.09375 L 255.039062 536.011719 L 257.242188 531.402344 C 261.847656 521.738281 261.804688 520.351562 256.914062 510.402344 L 255.082031 506.648438 L 285.117188 506.648438 L 285.363281 505.304688 C 285.484375 504.609375 286.300781 498.207031 287.195312 491.152344 C 288.09375 484.097656 289.113281 477.53125 289.480469 476.59375 C 290.210938 474.554688 292.820312 472.148438 294.898438 471.578125 C 296.773438 471.046875 537.511719 471.089844 539.265625 471.617188 Z M 539.265625 471.617188',
  HEART:
    'M1771.731 291.037C1675.709 193.659 1547.944 140 1411.818 140h-.113c-136.125 0-263.777 53.66-359.573 150.924-37.618 38.07-68.571 80.997-92.294 127.426-23.61-46.429-54.563-89.356-92.068-127.313C771.86 193.659 644.208 140 507.97 140h-.113c-136.012 0-263.777 53.66-359.8 151.037-197.691 200.629-197.691 527.103 1.695 729.088l810.086 760.154 811.893-761.736c197.692-200.403 197.692-526.877 0-727.506',
  HOUSE:
    'm379.69,168.56c-.62-.73-1.3-1.4-1.99-2.06-9.02-8.53-18.07-17.04-27.07-25.59-10.54-10-21.04-20.04-31.5-30,0-1.26,0-2.31,0-3.37,0-33.22,0-66.44,0-99.66,0-4.69-1.49-6.4-5.89-6.43-9.76-.07-19.53-.09-29.29.02-5.13.05-6.64,1.71-6.63,6.77.02,20.8.05,41.61.06,62.41,0,.75.46,1.71-.63,2.23-.42-.35-.84-.67-1.22-1.04-23.33-22.28-46.93-44.3-69.82-67.03-6.06-6.02-22.4-6.76-27.79.08-2.73,3.46-6.37,6.21-9.63,9.25C122.38,56.88,76.49,99.65,30.6,142.4c-8.93,8.32-17.92,16.57-26.84,24.9-2.96,2.77-4.84,6.07-3.07,10.23,1.71,4,5.24,4.92,9.19,4.89.96,0,1.91-.01,2.87-.01,8.37,0,16.75,0,25.67,0,0,2.39,0,4.28,0,6.16-.03,46.59-.06,93.19-.08,139.78,0,6.03,1.13,7.19,6.92,7.19,97.33,0,194.66.01,291.98.02.64,0,1.28.01,1.91-.03,2.57-.16,4.17-1.44,4.45-4.08.13-1.26.15-2.54.15-3.81-.01-47.02-.04-94.04-.03-141.06,0-1.32-.41-2.74.63-4.24,1.23,0,2.59,0,3.95,0,8.49.02,16.98.06,25.47.05,3.31,0,6.09-1.22,7.52-4.38,1.55-3.44.79-6.63-1.61-9.46Z',
};

export const PictorialWidthCalculation = {
  3: { width: 35, scale: 0.54 },
  4: { width: 28, scale: 0.7 },
  5: { width: 24, scale: 0.8 },
  6: { width: 24, scale: 0.8 },
};

export const PictorialComparisonWidthCalculation = {
  3: { scale: 0.4, width: 23 },
  4: { scale: 0.52, width: 19 },
  5: { scale: 0.52, width: 19 },
  6: { scale: 0.7, width: 13.83 },
  7: { scale: 0.7, width: 13.83 },
  8: { scale: 0.7, width: 13.83 },
  9: { scale: 0.7, width: 13.83 },
  10: { scale: 0.7, width: 13.83 },
  11: { scale: 0.7, width: 13.83 },
};

export const IS_ZOOM_FUNCTIONALITY_DISABLED = true;
